import React, { useEffect, useState } from "react";
import Card from "src/components/Cards/Basic";
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  Row,
  Spinner,
} from "react-bootstrap";
import TimeSwitcher from "./TimeSwitcher";
import MainCalendar from "./MainCalendar";
import { keepState } from "../../../../components/UiComponents/Buttons/BackButton/helper";
import { matchIcon, pixelsToRem } from "./TimeSwitcher/Helpers/util";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsLeftRight,
  faMinus,
  faPlus,
  faSearch,
  faTriangleExclamation as WarnIcon,
  faUmbrellaBeach,
} from "@fortawesome/free-solid-svg-icons";
import history from "../../../../global/history";
import RollingWeekDriver from "./TimeSwitcher/SwitcherDrivers/RollingWeekDriver";
import useFilter from "../../../../global/useFilter";
import { useLocation } from "react-router-dom";
import axios from "../../../../config/axios";
import { useDebounce } from "use-debounce";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { navigateTo } from "src/global/navigationHelper";

export default function BookingPlannerPage() {
  const { t } = useTranslation("dashboard");
  const [headerUnits, setHeaderUnits] = useState([]);
  const [currentMarked, setCurrentMarked] = useState(-1);

  const [widthInterrupted, setWidthInterrupted] = useState(false);
  const [unitWidth, setUnitWidth] = useState(0);
  const widthStyle = { width: unitWidth + "rem", minWidth: unitWidth + "rem" };

  const [isLoading, setIsLoading] = useState(true);

  const location = useLocation();
  const { pathname } = location;

  function getDefaultFilter() {
    const center = moment();
    return {
      search: "",
      parking_slot_status: "all",
      from: center.clone().subtract(1, "week").startOf("isoWeek"),
      to: center.clone().add(2, "weeks").endOf("isoWeek"),
    };
  }

  const { filter, setFilter } = useFilter(pathname, getDefaultFilter());

  const [driverInstance, setDriverInstance] = useState(
    () => new RollingWeekDriver(filter.from, filter.to)
  );

  const [searchValue] = useDebounce(filter.search, 400);

  const [parkingSlots, setParkingSlots] = useState([]);
  const [sortedDataset, setSortedDataset] = useState([]);

  async function getDataset() {
    setIsLoading(true);

    const result = await axios.get("/api/v1/bookings/booking_overview.json", {
      params: filter,
    });
    setParkingSlots(result.data.entries.map((entry) => entry.parking_slot));
    setHeaderUnits(result.data.headers);

    let dataset = {};
    result.data.entries.forEach((entry) => {
      dataset[entry.parking_slot.id] = entry.bookings;
    });
    setSortedDataset(dataset);
    setIsLoading(false);
  }

  useEffect(() => {
    getDataset();
  }, [filter.from, filter.to, filter.parking_slot_status, searchValue]);

  const minZoom = 3;
  const maxZoom = unitWidth * 3;
  const zoomStep = 0.5;

  useEffect(() => {
    if (widthInterrupted) return;

    calculateWidth();
    window.addEventListener("resize", calculateWidth);
    return () => window.removeEventListener("resize", calculateWidth);
  }, [headerUnits, widthInterrupted]);

  function calculateWidth() {
    if (headerUnits.length > 0) {
      const clientWidth = document.getElementById("CalendarParent").clientWidth;
      const firstColumnWidth =
        document.getElementById("long_field").offsetWidth;
      const width =
        pixelsToRem(clientWidth - firstColumnWidth) / headerUnits.length;
      setUnitWidth(width > minZoom ? width : minZoom);
    }
  }

  function resetZoom() {
    setWidthInterrupted(false);
  }

  function zoomOut() {
    if (!widthInterrupted) {
      setWidthInterrupted(true);
    }
    let new_zoom = unitWidth - zoomStep;
    if (new_zoom < minZoom) {
      new_zoom = minZoom;
    }
    setUnitWidth(new_zoom);
  }

  function zoomIn() {
    if (!widthInterrupted) {
      setWidthInterrupted(true);
    }
    let new_zoom = unitWidth + zoomStep;
    if (new_zoom > maxZoom) {
      new_zoom = maxZoom;
    }
    setUnitWidth(new_zoom);
  }

  const search = (search) => {
    let sanitizedSearch = search;
    sanitizedSearch = sanitizedSearch.replaceAll(":", "");

    setFilter({ ...filter, search: sanitizedSearch });
  };

  function highlightCol(col_id) {
    setCurrentMarked(currentMarked === col_id ? -1 : col_id);
  }

  function switcherChange(updated) {
    setFilter({ ...filter, from: updated.from, to: updated.to });
  }

  function createBooking() {
    navigateTo("/dashboard/bookings/new");
  }

  const zoomer = (
    <div className="ui-zoom-container">
      <div
        onClick={resetZoom}
        className={widthInterrupted ? "ui-zoom-button" : "ui-zoom-disabled"}
      >
        <FontAwesomeIcon icon={faArrowsLeftRight} />
      </div>
      <div
        onClick={zoomOut}
        className={
          unitWidth !== minZoom ? "ui-zoom-button" : "ui-zoom-disabled"
        }
      >
        <FontAwesomeIcon icon={faMinus} />
      </div>
      <div
        onClick={zoomIn}
        className={
          unitWidth !== maxZoom ? "ui-zoom-button" : "ui-zoom-disabled"
        }
      >
        <FontAwesomeIcon icon={faPlus} />
      </div>
    </div>
  );

  const headerUnit = (header, index) => (
    <div
      style={
        header.today
          ? { backgroundColor: "var(--ui-overview-today)" }
          : header.weekend
          ? { backgroundColor: "var(--ui-overview-weekend)" }
          : header.is_holiday
          ? { backgroundColor: "var(--ui-overview-holiday)" }
          : {}
      }
      className={`ui-calendar-tile ui-calendar-tile-color force-pointer user-select-none ${
        currentMarked === index ? "ui-bg-overview-selected" : ""
      }`}
    >
      <span className="w-100 d-flex justify-content-center text-center mt-0 mb-0 text-nano force-pointer">
        {header.today && "Heute"}
      </span>
      <span
        className={
          "w-100 d-flex justify-content-center ui-text-mini force-pointer" +
          (header.weekend ? " font-weight-bold text-dark" : "")
        }
      >
        {header.is_holiday ? (
          <FontAwesomeIcon icon={faUmbrellaBeach} />
        ) : (
          header.day_name
        )}
      </span>
      <b>{header.day}</b>
    </div>
  );

  const header = (
    <div className="d-flex w-100 table-header" id="header_bar">
      <div
        id="long_field"
        className="slot-col ui-unit ui-header-unit first-col"
      >
        <TimeSwitcher
          driverInstance={driverInstance}
          callbackUpdateValues={switcherChange}
        />
      </div>
      {headerUnits?.map((h_unit, index) => (
        <div
          style={widthStyle}
          onClick={() => {
            highlightCol(index);
          }}
          className="ui-calendar-unit ui-header-unit force-pointer"
          key={index}
        >
          {h_unit.is_holiday ? (
            <OverlayTrigger
              key={"top"}
              placement={"top"}
              overlay={
                <Popover id={`popover-positioned-top`}>
                  <div className="popover-body">
                    {h_unit.holidays.map((holiday, index) => (
                      <div key={index}>
                        <FontAwesomeIcon
                          className="fa-fw mr-2"
                          color={holiday.color}
                          icon={faUmbrellaBeach}
                        />
                        {holiday.name}
                      </div>
                    ))}
                  </div>
                </Popover>
              }
            >
              {headerUnit(h_unit, index)}
            </OverlayTrigger>
          ) : (
            <>{headerUnit(h_unit, index)}</>
          )}
        </div>
      ))}
    </div>
  );

  const title = (
    <div className="w-100 d-flex flex-wrap justify-content-between align-items-center font-weight-bold text-primary">
      <div className="align-items-center d-flex mr-3 my-1">
        Buchungs Übersicht ({parkingSlots?.length ?? 0})
      </div>
      <div className="d-flex align-items-center flex-shrink-0 my-1">
        {zoomer}
        <Button className="ml-3 text-nowrap" onClick={createBooking}>
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          {t("booking.new")}
        </Button>
      </div>
    </div>
  );

  const footer = (
    <div className="w-full d-flex justify-content-between align-items-center">
      <div className="w-full d-flex align-items-center flex-wrap justify-content-between">
        <div className="d-flex w-auto flex-wrap">
          <div className="d-flex align-items-center border-secondary pr-2 pl-2 pt-1 pt-md-0">
            <div
              className="ui-legend-box ui-hover-scale-116 ui-hover-transition-025 ui-border-1 border-bottom-4"
              style={{
                backgroundColor: "var(--ui-state-blue)",
                borderColor: "var(--ui-state-border-blue)",
              }}
            ></div>
            <span>&nbsp; Aktive Buchung</span>
          </div>
          <div className="d-flex align-items-center border-secondary pr-2 pl-2 pt-1 pt-md-0">
            <div
              className="ui-legend-box ui-hover-scale-116 ui-hover-transition-025 ui-border-1 border-bottom-4"
              style={{
                backgroundColor: "var(--ui-state-gray)",
                borderColor: "var(--ui-state-border-gray)",
              }}
            ></div>
            <span>&nbsp; Abgeschlossen</span>
          </div>
          <div className="d-flex align-items-center border-secondary pr-2 pl-2 pt-1 pt-md-0">
            <div
              className="ui-legend-box ui-hover-scale-116 ui-hover-transition-025 ui-border-1 border-bottom-4"
              style={{
                backgroundColor: "var(--ui-state-orange)",
                borderColor: "var(--ui-state-border-orange)",
              }}
            ></div>
            <span>&nbsp; Res. angefragt</span>
          </div>
          <div className="d-flex align-items-center border-secondary pr-2 pl-2 pt-1 pt-md-0">
            <div
              className="ui-legend-box ui-hover-scale-116 ui-hover-transition-025 ui-border-1 border-bottom-4"
              style={{
                backgroundColor: "var(--ui-state-green)",
                borderColor: "var(--ui-state-border-green)",
              }}
            ></div>
            <span>&nbsp; Res. akzeptiert</span>
          </div>
          <div className="d-flex align-items-center border-secondary pr-2 pl-2 pt-1 pt-md-0">
            <div
              className="ui-legend-box ui-hover-scale-116 ui-hover-transition-025 ui-border-1 border-bottom-4"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg, #f0184abf, #f0184abf 5px, rgba(244,33,81,0.3) 5px, rgba(244,33,81,0.3) 10px)",
                borderColor: "rgb(244,33,81)",
              }}
            ></div>
            <span>&nbsp; Buchung überzogen</span>
          </div>
          <div className="d-flex align-items-center border-secondary pr-2 pl-2 pt-1 pt-md-0">
            <FontAwesomeIcon icon={WarnIcon} className={"text-warning"} />
            <span>&nbsp; Check-In ausstehend</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={"booking-planner"}>
      <Card title={title} footer={footer}>
        <div className={"d-flex filter-container"}>
          <div className="col-12 col-md-6 col-xl-4 pl-0 mb-2">
            <div className="position-relative d-flex">
              <div className="position-absolute p-2">
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <Form.Control
                placeholder="#ID, Name, E-Mail, Karte"
                className="input-icon-p ui-input-bg"
                type="search"
                value={filter.search}
                onChange={(e) => search(e.target.value)}
              />
            </div>
          </div>

          <div className={"item"}>
            <Form.Control
              as={"select"}
              className="form-control ui-input-bg"
              value={filter.parking_slot_status ?? ""}
              onChange={(e) => {
                setFilter({
                  ...filter,
                  parking_slot_status:
                    e.target.value === "null" ? undefined : e.target.value,
                  page: 1,
                });
              }}
            >
              <option value="null">Alle Stellplätze</option>
              <option key={"available"} value="available">
                Freie Stellplätze
              </option>
              <option key={"occupied"} value="occupied">
                Aktuell besetzte Stellplätze
              </option>
            </Form.Control>
          </div>
        </div>
        <div className="booking-calendar-container" id="CalendarParent">
          {headerUnits.length === 0 ? (
            <Spinner
              className={"d-block mx-auto"}
              animation="border"
              variant="primary"
            />
          ) : (
            <div style={{ position: "relative" }}>
              <div
                style={isLoading ? { opacity: 0.3, pointerEvents: "none" } : {}}
              >
                {header}
                <div className="parking-slots-container justify-content-start align-items-start align-content-start">
                  <div className="d-flex w-100 auto-height background-container">
                    <div className="slot-col"></div>
                    {headerUnits.map((unit, index) => (
                      <div
                        style={
                          unit.today
                            ? {
                                backgroundColor: "var(--ui-overview-today)",
                                ...widthStyle,
                              }
                            : unit.weekend
                            ? {
                                backgroundColor: "var(--ui-overview-weekend)",
                                ...widthStyle,
                              }
                            : unit.is_holiday
                            ? {
                                backgroundColor: "var(--ui-overview-holiday)",
                                ...widthStyle,
                              }
                            : { ...widthStyle }
                        }
                        key={index}
                        className={`ui-unit ui-calendar-unit ${
                          currentMarked === index
                            ? "ui-bg-overview-selected"
                            : ""
                        }`}
                      ></div>
                    ))}
                  </div>

                  <MainCalendar
                    driver={driverInstance}
                    parkingSlots={parkingSlots}
                    sortedDataset={sortedDataset}
                    unitWidth={unitWidth}
                    numberOfUnits={headerUnits.length}
                    loading={isLoading}
                  />
                </div>
              </div>
              {isLoading && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Spinner animation="border" variant="primary" />
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
